import type { ConfigMotor } from "./config.ts";
import type { Conformidade, NivelMaturidade } from "./types.ts";

const NIVEIS: readonly NivelMaturidade[] = ["0", "1", "2", "3", "4", "na"];

const SITUACAO: Readonly<Record<NivelMaturidade, string>> = {
  "0": "Ausente",
  "1": "Estado Inicial",
  "2": "Em Estruturação",
  "3": "Consolidado",
  "4": "Otimizado",
  na: "Não Aplicável",
};

const CONFORMIDADE: Readonly<Record<Exclude<NivelMaturidade, "na">, Conformidade>> = {
  "0": "NC",
  "1": "NC",
  "2": "PC",
  "3": "C",
  "4": "CS",
};

/** Lançado quando um nível de maturidade inválido tenta entrar no domínio. */
export class MaturidadeInvalida extends Error {
  constructor(valor: unknown) {
    super(`Maturidade inválida: ${String(valor)}`);
    this.name = "MaturidadeInvalida";
  }
}

/**
 * Value object imutável e autovalidado. Carrega as regras da metodologia que
 * dependem só do nível: curva ONA (score), conformidade, e a regra de ouro
 * "nota com score exige evidência" (doc 13.4).
 */
export class Maturidade {
  private constructor(readonly nivel: NivelMaturidade) {}

  static de(valor: string): Maturidade {
    const n = valor.charAt(0) === "n" ? "na" : valor.charAt(0);
    if (!NIVEIS.includes(n as NivelMaturidade)) {
      throw new MaturidadeInvalida(valor);
    }
    return new Maturidade(n as NivelMaturidade);
  }

  get ehNA(): boolean {
    return this.nivel === "na";
  }

  get situacao(): string {
    return SITUACAO[this.nivel];
  }

  /** Conformidade derivada; vazia para NA. */
  get conformidade(): Conformidade | null {
    if (this.ehNA) {
      return null;
    }
    return CONFORMIDADE[this.nivel as Exclude<NivelMaturidade, "na">];
  }

  /** Score pela curva ONA da config. `null` quando NA (excluído do cálculo). */
  score(config: ConfigMotor): number | null {
    if (this.ehNA) {
      return null;
    }
    return config.curvaScore[this.nivel as "0" | "1" | "2" | "3" | "4"];
  }

  /** Regra de ouro: todo nível com score atribuído exige evidência. */
  get exigeEvidencia(): boolean {
    return !this.ehNA;
  }
}
